import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Arch, type ArchLane } from '~/table/classic/Arch.js';
import { ARCH, ARCH_FINISHES, archLane, type ArchFinish } from '~/table/classic/archGeometry.js';
import { Button } from '~/ui/Button.js';
import { FieldLabel } from '~/ui/FieldLabel.js';

/**
 * The visual setting for the Classic board's finish.
 *
 * Graduated from the `lobby-settings` sketch. The preview is the table's own
 * `Arch`, cropped to fifteen holes of its left leg, so what the player picks
 * is drawn by the code that draws the game. The arrows, the name and the
 * position work the way `CardBackPicker`'s do, and wrap around the six.
 */

const TARGET = 121;

/** Both pegs at the start, below the crop: the preview shows the finish alone. */
const LANES: readonly ArchLane[] = [
  { tone: 'teal', score: 0, back: 0 },
  { tone: 'tangerine', score: 0, back: 0 },
];

/** Holes 5 to 19 of the outside lane, with a little of the rail beside them. */
const CROP = ((): string => {
  const top = archLane(ARCH.phone, 19.5, 0, LANES.length, TARGET).y;
  const bottom = archLane(ARCH.phone, 4.5, 0, LANES.length, TARGET).y;
  return `-6 ${String(top)} 45 ${String(bottom - top)}`;
})();

export function BoardFinishPicker({
  value,
  onChange,
  tag,
}: {
  readonly value: ArchFinish;
  readonly onChange: (value: ArchFinish) => void;
  /** A mark pinned to the preview's corner, as on the card pickers. Positions itself. */
  readonly tag?: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const labelId = useId();
  const currentIndex = Math.max(0, ARCH_FINISHES.indexOf(value));
  const current = ARCH_FINISHES[currentIndex] ?? value;

  const move = (offset: number): void => {
    const next =
      ARCH_FINISHES[(currentIndex + offset + ARCH_FINISHES.length) % ARCH_FINISHES.length];
    if (next !== undefined) onChange(next);
  };

  return (
    <div className="flex flex-col gap-3 py-2 text-ink" role="group" aria-labelledby={labelId}>
      <FieldLabel id={labelId}>{t('settings.chooseBoard')}</FieldLabel>

      <div className="flex items-center justify-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('settings.boardPrevious')}
          onClick={() => {
            move(-1);
          }}
        >
          <CaretLeftIcon className="size-4 shrink-0" aria-hidden="true" focusable="false" />
        </Button>

        <div className="relative h-56 max-w-40 min-w-0 flex-1">
          <div aria-hidden="true" className="size-full">
            <Arch
              size="phone"
              lanes={LANES}
              label=""
              target={TARGET}
              finish={current}
              crop={CROP}
            />
          </div>
          {tag}
        </div>

        <Button
          variant="ghost"
          size="sm"
          aria-label={t('settings.boardNext')}
          onClick={() => {
            move(1);
          }}
        >
          <CaretRightIcon className="size-4 shrink-0" aria-hidden="true" focusable="false" />
        </Button>
      </div>

      <div className="text-center" aria-live="polite">
        <p className="truncate text-sm leading-snug text-ink">
          {t(`settings.boardFinishes.${current}`)}
        </p>
        <p className="truncate text-sm text-gray-700">
          {t('settings.boardPosition', {
            current: currentIndex + 1,
            total: ARCH_FINISHES.length,
          })}
        </p>
      </div>
    </div>
  );
}
